/**
 * Table component type definitions
 * Business benefit: Type-safe, flexible table with sorting, filtering, and column management
 */

/**
 * Sort direction
 */
export type SortDirection = 'asc' | 'desc' | null;

/**
 * Sort configuration for a column
 */
export interface SortConfig<TData> {
  readonly key: keyof TData;
  readonly direction: SortDirection;
}

/**
 * Filter operator types
 */
export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual'
  | 'between'
  | 'in'
  | 'notIn';

/**
 * Filter configuration for a column
 */
export interface FilterConfig<TData> {
  readonly key: keyof TData;
  readonly operator: FilterOperator;
  readonly value: unknown;
}

/**
 * Column definition for table
 */
export interface ColumnDef<TData> {
  readonly id: string;
  readonly header: string;
  readonly accessorKey: keyof TData;
  readonly cell?: (row: TData) => React.ReactNode;
  readonly sortable?: boolean;
  readonly filterable?: boolean;
  readonly hidden?: boolean;
  readonly width?: string;
  readonly minWidth?: string;
  readonly align?: 'left' | 'center' | 'right';
  readonly className?: string;
  readonly headerClassName?: string;
}

/**
 * Column visibility state
 */
export interface ColumnVisibility {
  readonly [columnId: string]: boolean;
}

/**
 * Table configuration
 */
export interface TableConfig<TData> {
  readonly data: readonly TData[];
  readonly columns: readonly ColumnDef<TData>[];
  readonly sortable?: boolean;
  readonly filterable?: boolean;
  readonly selectable?: boolean;
  readonly expandable?: boolean;
  readonly stickyHeader?: boolean;
  readonly striped?: boolean;
  readonly hoverable?: boolean;
  readonly bordered?: boolean;
  readonly compact?: boolean;
  readonly loading?: boolean;
  readonly emptyMessage?: string;
  readonly mobileBreakpoint?: 'sm' | 'md' | 'lg';
  readonly onRowClick?: (row: TData) => void;
  readonly rowClassName?: (row: TData) => string;
}

/**
 * Table sort hook return type
 */
export interface UseTableSortReturn<TData> {
  readonly sortedData: readonly TData[];
  readonly sortConfig: SortConfig<TData> | null;
  readonly handleSort: (key: keyof TData) => void;
  readonly clearSort: () => void;
}

/**
 * Table filter hook return type
 */
export interface UseTableFilterReturn<TData> {
  readonly filteredData: readonly TData[];
  readonly filters: readonly FilterConfig<TData>[];
  readonly addFilter: (filter: FilterConfig<TData>) => void;
  readonly removeFilter: (key: keyof TData) => void;
  readonly clearFilters: () => void;
  readonly updateFilter: (
    key: keyof TData,
    operator: FilterOperator,
    value: unknown
  ) => void;
}

/**
 * Column visibility hook return type
 */
export interface UseColumnVisibilityReturn {
  readonly visibleColumns: readonly string[];
  readonly columnVisibility: ColumnVisibility;
  readonly toggleColumn: (columnId: string) => void;
  readonly showColumn: (columnId: string) => void;
  readonly hideColumn: (columnId: string) => void;
  readonly resetColumns: () => void;
  readonly hideAllColumns: () => void;
  readonly showAllColumns: () => void;
}

/**
 * Table pagination configuration
 */
export interface PaginationConfig {
  readonly currentPage: number;
  readonly pageSize: number;
  readonly totalItems: number;
  readonly totalPages: number;
}

/**
 * Table row selection configuration
 */
export interface SelectionConfig<TData> {
  readonly selectedRows: readonly TData[];
  readonly isRowSelected: (row: TData) => boolean;
  readonly toggleRow: (row: TData) => void;
  readonly selectAll: () => void;
  readonly clearSelection: () => void;
}

/**
 * Table row density variants
 */
export enum TableDensity {
  Compact = 'compact',
  Default = 'default',
  Comfortable = 'comfortable',
}

/**
 * Table row state variants
 */
export enum TableRowVariant {
  Default = 'default',
  Hover = 'hover',
  Selected = 'selected',
}
